/**
 * Calendar - Monthly calendar widget with match indicators
 *
 * Features:
 * - Outlook-style monthly view
 * - Hover tooltip showing matches for that day
 * - Expand to full calendar view
 * - Color coding for event types
 * - Today and selected date highlighting
 */

import { useState, useMemo, useCallback } from 'react';
import clsx from 'clsx';
import { isMatchOnDate, isToday, isSameDay, formatTime, getMatchStatus } from '../../utils/dateUtils';
import { WEEKDAYS_SHORT, MONTHS, getEventColor } from '../../utils/constants';
import styles from './Calendar.module.css';

/**
 * Tooltip showing matches for a specific day
 */
function DayTooltip({ matches, date, onMatchClick }) {
  if (!matches || matches.length === 0) return null;

  return (
    <div className={styles.tooltip}>
      <div className={styles.tooltipHeader}>
        {date.toLocaleDateString('nl-NL', { weekday: 'short', day: 'numeric', month: 'short' })}
      </div>
      <div className={styles.tooltipMatches}>
        {matches.slice(0, 5).map((match) => (
          <button
            key={match.id}
            className={styles.tooltipMatch}
            onClick={(e) => {
              e.stopPropagation();
              onMatchClick?.(match);
            }}
          >
            <span
              className={styles.tooltipDot}
              style={{ background: getEventColor(match) }}
            />
            <span className={styles.tooltipTime}>{formatTime(match.start_time)}</span>
            <span className={styles.tooltipName}>
              {match.home_team_name || match.name || 'Match'}
              {match.away_team_name && ` vs ${match.away_team_name}`}
            </span>
          </button>
        ))}
        {matches.length > 5 && (
          <div className={styles.tooltipMore}>+{matches.length - 5} more</div>
        )}
      </div>
    </div>
  );
}

/**
 * Single day cell in the calendar
 */
function CalendarDay({
  date,
  matches,
  isCurrentMonth,
  isToday,
  isSelected,
  showNonScheduled,
  onSelect,
  onMatchClick,
}) {
  const [showTooltip, setShowTooltip] = useState(false);

  const dayMatches = useMemo(() => {
    if (!date || !matches) return [];
    let filtered = matches.filter((m) => isMatchOnDate(m, date));
    if (!showNonScheduled) {
      filtered = filtered.filter((m) => m.is_scheduled !== false);
    }
    return filtered;
  }, [date, matches, showNonScheduled]);

  const hasLive = dayMatches.some((m) => getMatchStatus(m) === 'live');
  const hasMatches = dayMatches.length > 0;

  // Get unique colors for dots
  const dotColors = useMemo(() => {
    const colors = [...new Set(dayMatches.map(getEventColor))];
    return colors.slice(0, 3); // Max 3 dots
  }, [dayMatches]);

  if (!date) {
    return <div className={styles.dayEmpty} />;
  }

  return (
    <div
      className={clsx(
        styles.day,
        !isCurrentMonth && styles.dayOtherMonth,
        isToday && styles.dayToday,
        isSelected && styles.daySelected,
        hasMatches && styles.dayHasMatches,
        hasLive && styles.dayLive
      )}
      onClick={() => onSelect?.(date)}
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      <span className={styles.dayNumber}>{date.getDate()}</span>
      {hasMatches && (
        <div className={styles.dayDots}>
          {dotColors.map((color) => (
            <span
              key={color}
              className={styles.dayDot}
              style={{ background: color }}
            />
          ))}
        </div>
      )}
      {showTooltip && hasMatches && (
        <DayTooltip matches={dayMatches} date={date} onMatchClick={onMatchClick} />
      )}
    </div>
  );
}

/**
 * Main Calendar component
 */
export default function Calendar({
  matches = [],
  selectedDate,
  onDateSelect,
  onMatchClick,
  showNonScheduled = false,
  expanded = false,
  onExpandToggle,
}) {
  const [currentMonth, setCurrentMonth] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });

  const year = currentMonth.getFullYear();
  const month = currentMonth.getMonth();

  // Navigate months
  const goToPrevMonth = useCallback(() => {
    setCurrentMonth(new Date(year, month - 1, 1));
  }, [year, month]);

  const goToNextMonth = useCallback(() => {
    setCurrentMonth(new Date(year, month + 1, 1));
  }, [year, month]);

  const goToToday = useCallback(() => {
    const now = new Date();
    setCurrentMonth(new Date(now.getFullYear(), now.getMonth(), 1));
    onDateSelect?.(now);
  }, [onDateSelect]);

  // Build calendar grid
  const calendarDays = useMemo(() => {
    const days = [];
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDayOfWeek = firstDay.getDay();

    // Add days from previous month
    const prevMonth = new Date(year, month, 0);
    for (let i = startDayOfWeek - 1; i >= 0; i--) {
      days.push({
        date: new Date(year, month - 1, prevMonth.getDate() - i),
        isCurrentMonth: false,
      });
    }

    // Add days of current month
    for (let d = 1; d <= lastDay.getDate(); d++) {
      days.push({
        date: new Date(year, month, d),
        isCurrentMonth: true,
      });
    }

    // Add days from next month to complete the grid
    const remaining = 42 - days.length; // 6 weeks * 7 days
    for (let d = 1; d <= remaining; d++) {
      days.push({
        date: new Date(year, month + 1, d),
        isCurrentMonth: false,
      });
    }

    return days;
  }, [year, month]);

  return (
    <div className={clsx(styles.calendar, expanded && styles.expanded)}>
      {/* Header */}
      <div className={styles.header}>
        <button
          className={styles.navBtn}
          onClick={goToPrevMonth}
          aria-label="Previous month"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>

        <div className={styles.title}>
          <span className={styles.monthYear}>
            {MONTHS[month]} {year}
          </span>
          <button className={styles.todayBtn} onClick={goToToday}>
            Today
          </button>
        </div>

        <button
          className={styles.navBtn}
          onClick={goToNextMonth}
          aria-label="Next month"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="9 18 15 12 9 6" />
          </svg>
        </button>

        {onExpandToggle && (
          <button
            className={styles.expandBtn}
            onClick={onExpandToggle}
            aria-label={expanded ? 'Collapse calendar' : 'Expand calendar'}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              {expanded ? (
                <path d="M8 3v3a2 2 0 0 1-2 2H3m18 0h-3a2 2 0 0 1-2-2V3m0 18v-3a2 2 0 0 1 2-2h3M3 16h3a2 2 0 0 1 2 2v3" />
              ) : (
                <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" />
              )}
            </svg>
          </button>
        )}
      </div>

      {/* Weekday headers */}
      <div className={styles.weekdays}>
        {WEEKDAYS_SHORT.map((day) => (
          <div key={day} className={styles.weekday}>
            {day}
          </div>
        ))}
      </div>

      {/* Calendar grid */}
      <div className={styles.grid}>
        {calendarDays.map(({ date, isCurrentMonth }) => (
          <CalendarDay
            key={date.getTime()}
            date={date}
            matches={matches}
            isCurrentMonth={isCurrentMonth}
            isToday={isToday(date)}
            isSelected={isSameDay(date, selectedDate)}
            showNonScheduled={showNonScheduled}
            onSelect={onDateSelect}
            onMatchClick={onMatchClick}
          />
        ))}
      </div>
    </div>
  );
}
